import { useEffect, useState } from "react";
import { CheckCircle2, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { getCategories, sendManuscript, apiError } from "@/lib/api";
import Seo from "@/components/Seo";

const HERO = "https://images.unsplash.com/photo-1550592704-6c76defa9985?crop=entropy&cs=srgb&fm=jpg&q=80&w=2400&ixlib=rb-4.1.0";

const SERVICIOS = [
  "Evaluación editorial", "Corrección y revisión", "Edición", "Maquetación",
  "Diseño de portada", "Preparación de archivos", "Gestión editorial", "Publicación",
  "Libro impreso", "Libro digital", "Distribución", "Promoción",
];

const PROCESO = [
  { t: "Envíanos tu manuscrito", d: "Completa el formulario y adjunta tu obra." },
  { t: "Evaluación editorial", d: "Nuestro equipo estudiará el proyecto y valorará su posible publicación." },
  { t: "Propuesta editorial", d: "Si el proyecto encaja con nuestra línea editorial, contactaremos con el autor para explicar las condiciones." },
  { t: "Producción", d: "Comienza el proceso de edición y preparación del libro." },
  { t: "Publicación", d: "Una vez finalizado el proceso editorial, la obra estará preparada para su publicación." },
];

export default function Publica() {
  const [categories, setCategories] = useState([]);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nombre: "", apellidos: "", email: "", pais: "", telefono: "",
    titulo: "", categoria: "", paginas: "", descripcion_obra: "", biografia_autor: "",
    manuscrito_terminado: "no",
  });
  const [file, setFile] = useState(null);
  const [authorship, setAuthorship] = useState(false);

  useEffect(() => {
    getCategories().then(setCategories);
    if (window.location.hash === "#enviar") {
      setTimeout(() => document.getElementById("enviar")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Debes adjuntar tu manuscrito.");
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) return toast.error("Formato no válido. Usa PDF, DOC o DOCX.");
    if (!authorship) return toast.error("Debes declarar la autoría de la obra.");

    setLoading(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "manuscrito_terminado") fd.append(k, v === "si" ? "true" : "false");
      else fd.append(k, v);
    });
    fd.append("file", file);
    try {
      await sendManuscript(fd);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-[#0f2544]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1f44e0] transition-colors";
  const labelCls = "block text-sm font-medium text-[#0f2544] mb-1.5";

  if (sent) {
    return (
      <>
        <Seo title="Manuscrito recibido" />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="manuscript-confirmation">
          <CheckCircle2 size={56} className="mx-auto text-[#1f44e0]" />
          <h1 className="font-serif text-3xl md:text-4xl text-[#0f2544] mt-6">Hemos recibido tu manuscrito</h1>
          <p className="text-[#4a4a45] mt-4 leading-relaxed">
            Gracias por confiar en Editorial Harvest. Nuestro equipo revisará la información enviada y se
            pondrá en contacto contigo si el proyecto encaja con nuestra línea editorial.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title="Publica con Harvest" description="Envía tu manuscrito a Editorial Harvest." />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a1b33]/80" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 pt-40 pb-24 md:pt-52 md:pb-32 text-center">
          <p className="hv-overline hv-overline-light">Publica con Harvest</p>
          <h1 className="font-serif font-light text-white text-4xl md:text-6xl lg:text-7xl leading-[1.02] mt-6 tracking-tight">
            Tu manuscrito puede ser el comienzo de algo grande
          </h1>
          <p className="text-white/80 text-lg md:text-xl mt-8 max-w-2xl mx-auto font-light leading-relaxed">
            Si has escrito un libro y buscas una editorial que cuide tu obra, queremos conocerla.
          </p>
          <a href="#enviar" className="group inline-flex items-center gap-2 mt-10 bg-white px-9 py-4 text-xs font-semibold tracking-[0.16em] text-[#0f2544] hover:bg-[#a9b4ff] hover:text-white transition-colors" data-testid="hero-enviar-btn">
            ENVÍA TU MANUSCRITO <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      {/* MÁS QUE IMPRIMIR */}
      <section className="bg-[#eceae4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-16 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#0f2544]">Publicar es mucho más que imprimir un libro</h2>
          <div className="mt-6 space-y-4 text-[#4a4a45] leading-relaxed">
            <p>Un libro necesita contenido, pero también necesita estructura, revisión, diseño, identidad y una presentación profesional.</p>
            <p>Nuestro objetivo es acompañar cada proyecto para que el resultado final respete la voz del autor y alcance el nivel editorial que merece.</p>
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-20">
        <div className="text-center">
          <p className="hv-overline">Servicios editoriales</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0f2544] mt-3">Podemos acompañarte en el proceso</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-10">
          {SERVICIOS.map((s) => (
            <div key={s} className="rounded-lg border border-[#0f2544]/10 bg-white px-4 py-4 text-sm text-[#0f2544] text-center" data-testid={`servicio-${s}`}>
              {s}
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-[#8a8a83] mt-8 max-w-2xl mx-auto italic">
          Los servicios y condiciones dependerán de las características y necesidades de cada proyecto editorial.
        </p>
      </section>

      {/* PROCESO */}
      <section className="bg-[#0f2544] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-24">
          <h2 className="font-serif text-3xl md:text-4xl text-center">¿Cómo publicar con Harvest?</h2>
          <div className="mt-12 space-y-8">
            {PROCESO.map((p, i) => (
              <div key={i} className="flex gap-5 items-start" data-testid={`proceso-${i + 1}`}>
                <div className="shrink-0 w-11 h-11 rounded-full border border-[#a9b4ff] text-[#a9b4ff] font-serif text-lg flex items-center justify-center">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-serif text-xl text-white">{p.t}</h3>
                  <p className="text-[#9aa6b6] mt-1 leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMULARIO */}
      <section id="enviar" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-24 scroll-mt-24">
        <div className="text-center mb-10">
          <p className="hv-overline">Formulario</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0f2544] mt-3">Envía tu manuscrito</h2>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="manuscript-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className={labelCls}>Nombre *</label><input required value={form.nombre} onChange={set("nombre")} className={inputCls} data-testid="ms-nombre" /></div>
            <div><label className={labelCls}>Apellidos *</label><input required value={form.apellidos} onChange={set("apellidos")} className={inputCls} data-testid="ms-apellidos" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className={labelCls}>Email *</label><input type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="ms-email" /></div>
            <div><label className={labelCls}>País *</label><input required value={form.pais} onChange={set("pais")} className={inputCls} data-testid="ms-pais" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className={labelCls}>Teléfono</label><input value={form.telefono} onChange={set("telefono")} className={inputCls} data-testid="ms-telefono" /></div>
            <div><label className={labelCls}>Título provisional del libro *</label><input required value={form.titulo} onChange={set("titulo")} className={inputCls} data-testid="ms-titulo" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Categoría *</label>
              <select required value={form.categoria} onChange={set("categoria")} className={inputCls} data-testid="ms-categoria">
                <option value="">Selecciona...</option>
                {categories.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Número aproximado de páginas</label><input value={form.paginas} onChange={set("paginas")} className={inputCls} data-testid="ms-paginas" /></div>
          </div>
          <div><label className={labelCls}>Breve presentación de la obra *</label><textarea required rows={4} value={form.descripcion_obra} onChange={set("descripcion_obra")} className={inputCls} data-testid="ms-descripcion" /></div>
          <div><label className={labelCls}>Breve presentación del autor *</label><textarea required rows={3} value={form.biografia_autor} onChange={set("biografia_autor")} className={inputCls} data-testid="ms-biografia" /></div>
          <div>
            <label className={labelCls}>¿El manuscrito está terminado?</label>
            <div className="flex gap-6 mt-1">
              <label className="flex items-center gap-2 text-sm text-[#0f2544]"><input type="radio" name="terminado" checked={form.manuscrito_terminado === "si"} onChange={() => setForm({ ...form, manuscrito_terminado: "si" })} className="accent-[#1f44e0]" data-testid="ms-terminado-si" /> Sí</label>
              <label className="flex items-center gap-2 text-sm text-[#0f2544]"><input type="radio" name="terminado" checked={form.manuscrito_terminado === "no"} onChange={() => setForm({ ...form, manuscrito_terminado: "no" })} className="accent-[#1f44e0]" /> No</label>
            </div>
          </div>
          <div>
            <label className={labelCls}>Adjuntar manuscrito * (PDF, DOC, DOCX)</label>
            <label className="flex items-center gap-3 rounded-lg border border-dashed border-[#0f2544]/25 bg-white px-4 py-4 cursor-pointer hover:border-[#1f44e0] transition-colors" data-testid="ms-file-label">
              <FileText size={18} className="text-[#1f44e0]" />
              <span className="text-sm text-[#4a4a45]">{file ? file.name : "Seleccionar archivo..."}</span>
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="ms-file" />
            </label>
          </div>
          <label className="flex items-start gap-2.5 text-sm text-[#4a4a45] cursor-pointer">
            <input type="checkbox" checked={authorship} onChange={(e) => setAuthorship(e.target.checked)} className="accent-[#1f44e0] mt-0.5" data-testid="ms-authorship" />
            Declaro que soy autor de la obra o que dispongo de autorización suficiente para presentarla a Editorial Harvest.
          </label>
          <button type="submit" disabled={loading} className="w-full rounded-full bg-[#0f2544] px-8 py-4 text-xs font-semibold tracking-[0.12em] text-white hover:bg-[#1f44e0] transition-colors disabled:opacity-60" data-testid="ms-submit">
            {loading ? "ENVIANDO..." : "ENVIAR MANUSCRITO"}
          </button>
        </form>
      </section>
    </>
  );
}
